'use client';

import { useEffect, useState } from 'react';
import { ShoppingCart } from '@pixum/web-header';
import { sessionClient } from 'services/api/client';
import { useCartStore } from 'stores/cart';
import { SESSION_JWT_STATUS, SESSION_REQUEST_TIMEOUT_MS, useSessionJwt } from 'hooks';
import { logger, withTimeout } from 'utils';
import { ShoppingCartStreamingSkeleton } from './ShoppingCartStreamingSkeleton';

type ShoppingCartClientProps = {
  desktopVariant?: boolean;
};

/**
 * ShoppingCartClient — client-only counterpart of ShoppingCartStreaming for pages without a server session.
 *
 * Seeds useCartStore from the cart overview once useSessionJwt has a session and renders from the store, so
 * useAddToCart (which writes the store after an add) keeps the badge current without a refetch here.
 * A login on the page refetches it: the customer's cart can differ from the anonymous one, and router.refresh()
 * does not reach this client state.
 */
export function ShoppingCartClient({ desktopVariant }: ShoppingCartClientProps) {
  const [loaded, setLoaded] = useState(false);
  const mainArticleCount = useCartStore(state => state.mainArticleCount);
  const setMainArticleCount = useCartStore(state => state.setMainArticleCount);
  const { status, jwt, version, afterLogin } = useSessionJwt();

  useEffect(() => {
    if (status === SESSION_JWT_STATUS.LOADING) return;

    // Bounded so a hung session or PSI call releases the skeleton with the current count.
    if (!jwt) {
      setLoaded(true);

      return;
    }

    let cancelled = false;

    // useAddToCart may write the store while the overview is in flight; its count is newer than the overview's.
    // After a login the customer's count must replace the anonymous one, so there is no such guard then.
    const countAtStart = useCartStore.getState().mainArticleCount;

    const seed =(count: number) => {
      const isStale = !afterLogin && useCartStore.getState().mainArticleCount !== countAtStart;

      if (!cancelled && !isStale) setMainArticleCount(count);
    };

    withTimeout(sessionClient.getCartOverview(1), SESSION_REQUEST_TIMEOUT_MS)
      .then(overview => seed(overview.totalMainArticleCount))
      .catch(error => {
        logger.warn({ error }, '[ShoppingCartClient] Failed to fetch cart overview in time');
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
    // version, not jwt: a repeat login can deliver the same JWT string and must still refetch.
  }, [status, version]);

  if (!loaded) {
    return <ShoppingCartStreamingSkeleton desktopVariant={desktopVariant} />;
  }

  return <ShoppingCart orders={mainArticleCount} desktopVariant={desktopVariant} />;
}
